import {
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getSigninFormValidationSchema,
  signinFormDefaultValue,
  signinFormType,
} from './utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { UPasswordInput } from 'components/core/u-password-input';
import { Captcha } from 'features/captcha';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useToast } from 'hooks/use-toast';
import { useSigninMutation } from '../../hooks/use-auth';
import ErrorAlert from '../../../../components/blocks/error-alert/error-alert';
import { SignInResponse } from '../../services/auth-service';
import { SsoSignin } from 'pages/auth/signin/signin-sso';
import { GRANT_TYPES } from 'constant/auth';
import { LoginOption } from 'constant/sso';
import { useConfig } from 'hooks/use-config';
import { GDPRConsentDialog } from 'components/blocks/gdpr-consent-dialog';
import { trackEvent } from 'analytics/tracker';
import { EVENTS } from 'analytics/events';
import { useTheme } from 'components/core/theme-provider';

/**
 * SigninForm Component
 *
 * A comprehensive login form component that handles user authentication with email and password,
 * supports two-factor authentication flow, and implements CAPTCHA protection after failed attempts.
 *
 * Features:
 * - Email and password authentication
 * - Form validation with Zod schema
 * - Progressive security with CAPTCHA after 3 failed login attempts
 * - Two-factor authentication (2FA/MFA) integration
 * - Error handling with alert display
 * - Navigation to relevant routes based on authentication status
 * - Success notification via toast
 * - "Forgot password" link
 *
 * @returns {JSX.Element} The rendered login form with validation and security features
 *
 * @example
 * // Basic usage
 * <SigninForm />
 *
 * // Within an authentication page
 * <div className="auth-container">
 *   <h1>Welcome Back</h1>
 *   <SigninForm />
 *   <div className="auth-footer">
 *     <p>Don't have an account? <Link to="/signup">Sign up</Link></p>
 *   </div>
 * </div>
 */

type SigninProps = {
  loginOption?: LoginOption;
};

export const SigninForm = ({ loginOption }: SigninProps) => {
  const [searchParams] = useSearchParams();
  const isFirstTimeCall = useRef<boolean>(true);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useAuthStore();
  const { toast } = useToast();
  const { theme } = useTheme();

  // Helper function to handle navigation after successful login
  const navigateAfterLogin = () => {
    const gotoParam = searchParams.get('goto');
    if (gotoParam) {
      navigate(gotoParam, { replace: true });
    } else {
      navigate('/home', { replace: true });
    }
  };
  const [captchaToken, setCaptchaToken] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [showGDPRDialog, setShowGDPRDialog] = useState(false);
  const { configurationSettings } = useConfig();

  const captchaEnabled = configurationSettings.googleCaptchaSiteKey !== '';

  const form = useForm({
    defaultValues: signinFormDefaultValue,
    resolver: zodResolver(getSigninFormValidationSchema(t)),
  });

  const { isPending, mutateAsync, isError, errorDetails } = useSigninMutation();

  const handleCaptchaVerify = (token: SetStateAction<string>) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken('');
  };

  const handleGDPRConsent = async () => {
    setShowGDPRDialog(false);

    try {
      // Get the current form values
      const values = form.getValues();

      // Retry the login process with terms and conditions accepted
      const res = (await mutateAsync({
        grantType: 'password',
        username: values.username.toLowerCase(),
        password: values.password,
        termsAndConditionsAccepted: true,
      })) as SignInResponse;

      if (res?.enable_mfa) {
        navigate(
          `/verify-key?mfa_id=${res?.mfaId}&mfa_type=${res?.mfaType}&user_name=${values.username.toLowerCase()}`
        );
      } else {
        login(res.access_token, res.refresh_token);
        setTimeout(() => {
          navigateAfterLogin();
          toast({
            variant: 'success',
            title: t('SUCCESS'),
            description: t('LOGIN_SUCCESSFULLY'),
          });
        }, 100);
      }
    } catch (error: any) {
      console.error('Failed to login with consent:', error?.error.error);
      if (error?.error.error === 'invalid_username_password') {
        toast({
          variant: 'destructive',
          title: t('INVALID_CREDENTIALS', 'Login Failed'),
          description: t('EMAIL_PASSWORD_NOT_VALID', 'Failed to complete login. Please try again.'),
        });
      } else {
        toast({
          variant: 'destructive',
          title: t('GDPR_CONSENT_ERROR', 'Login Failed'),
          description: t('GDPR_CONSENT_ERROR_DESCRIPTION', 'Failed to complete login. Please try again.'),
        });
      }
    }
  };

  const handleGDPRDecline = () => {
    setShowGDPRDialog(false);
    toast({
      variant: 'destructive',
      title: t('USER_NOT_AGREED_TERMS_AND_CONDITIONS'),
      description: t('USER_NOT_AGREED_TERMS_AND_CONDITIONS_DESCRIPTION'),
    });
  };

  const onSubmitHandler = async (values: signinFormType) => {
    if (captchaEnabled && showCaptcha && !captchaToken) {
      return;
    }
    trackEvent(EVENTS.FORM_SUBMITTED("Signin Form Submitted"));
    try {
      const res = (await mutateAsync({
        grantType: 'password',
        username: values.username.toLowerCase(),
        password: values.password,
      })) as SignInResponse;

      if (res?.enable_mfa) {
        navigate(
          `/verify-key?mfa_id=${res?.mfaId}&mfa_type=${res?.mfaType}&user_name=${values.username.toLowerCase()}`
        );
      } else {
        login(res.access_token, res.refresh_token);
        setTimeout(() => {
          navigateAfterLogin();
          toast({
            variant: 'success',
            title: t('SUCCESS'),
            description: t('LOGIN_SUCCESSFULLY'),
          });
        }, 100);
      }
    } catch (_error: any) {
      if (_error.error.error === 'user_not_agreed_terms_and_conditions') {
        setShowGDPRDialog(true);
        return; // Don't increment failed attempts for consent issues
      }
      if (captchaEnabled) {
        const newFailedAttempts = failedAttempts + 1;
        setFailedAttempts(newFailedAttempts);

        if (newFailedAttempts >= 3 && !showCaptcha) {
          setShowCaptcha(true);
        }
      }
    }
  };

  const passwordGrantAllowed = loginOption?.allowedGrantTypes?.includes(GRANT_TYPES.password);
  const socialGrantAllowed = loginOption?.allowedGrantTypes?.includes(GRANT_TYPES.social);

  const signin = useCallback(
    async (code: string, state: string) => {
      const res = await mutateAsync({ grantType: 'social', code, state });
      login(res.access_token, res.refresh_token);
      // Check if there's a goto parameter in the URL
      const gotoParam = searchParams.get('goto');
      if (gotoParam) {
        navigate(gotoParam);
      } else {
        navigate('/home');
      }
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('LOGIN_SUCCESSFULLY'),
      });
    },
    [login, mutateAsync, navigate, searchParams, t, toast]
  );

  useEffect(() => {
    const state = searchParams.get('state');
    const code = searchParams.get('code');
    if (code && state && isFirstTimeCall.current) {
      isFirstTimeCall.current = false;
      signin(code, state);
    }
  }, [searchParams, signin]);

  return (
    <div className="w-full">
      <ErrorAlert isError={isError} title={errorDetails.title} message={errorDetails.message} />

      {/* Only render the form if password grant type is allowed */}
      {passwordGrantAllowed && (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmitHandler)} className="space-y-4">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('EMAIL')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('ENTER_YOUR_EMAIL')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('PASSWORD')}</FormLabel>
                  <FormControl>
                    <UPasswordInput placeholder={t('ENTER_YOUR_PASSWORD')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-primary hover:text-primary-600 hover:underline"
              >
                {t('FORGOT_PASSWORD')}
              </Link>
            </div>

            {captchaEnabled && showCaptcha && (
              <div className="my-4">
                <Captcha
                  type="reCaptcha"
                  siteKey={configurationSettings.googleCaptchaSiteKey}
                  theme="light"
                  onVerify={handleCaptchaVerify}
                  onExpired={handleCaptchaExpired}
                  size="normal"
                />
              </div>
            )}

            <Button
              type="submit"
              className={`w-full bg-primary-600 hover:bg-primary-700 ${theme === 'dark' ? 'text-neutral-800' : 'text-neutral-50'}`}
              disabled={isPending || (captchaEnabled && showCaptcha && !captchaToken)}
            >
              {t('LOG_IN')}
            </Button>
          </form>
        </Form>
      )}

      {socialGrantAllowed && loginOption && <SsoSignin loginOption={loginOption} />}

      {/* GDPR Consent Dialog */}
      <GDPRConsentDialog
        open={showGDPRDialog}
        onOpenChange={setShowGDPRDialog}
        onConsent={handleGDPRConsent}
        onDecline={handleGDPRDecline}
      />
    </div>
  );
};
